import {
  Badge,
  Card,
  CardHeader,
  Divider,
  makeStyles,
  Text,
  tokens,
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Activity } from "../../models/Activity";
import { ClientRoutes } from "../../models/ClientRoutes";
import { Field } from "../../models/Field";
import ActivityHistoryDiffCard from "./ActivityHistoryDiffCard";

const useStyles = makeStyles({
  metadata: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    paddingTop: tokens.spacingVerticalS,
  },
  metadataRow: {
    display: "flex",
    gap: tokens.spacingHorizontalL,
  },
  metadataLabel: {
    fontWeight: tokens.fontWeightSemibold,
    minWidth: "120px",
  },
  historyCard: {
    marginBottom: tokens.spacingVerticalL,
  },
  currentVersionLabel: {
    marginRight: tokens.spacingHorizontalS,
  },
});

interface ActivityHistoryListProps {
  activities: Activity[];
  activityFields: Field[];
}

const ActivityHistoryList = (props: ActivityHistoryListProps) => {
  const { activities, activityFields } = props;
  const { t } = useTranslation();
  const styles = useStyles();

  return (
    <div>
      {activities.map((activity, index) => {
        const isCurrent = index === 0; // Assuming here the first activity is the current one
        const previousActivity = activities[index + 1];

        return (
          <Card key={activity.versionId} className={styles.historyCard}>
            <CardHeader
              header={
                <div>
                  {!isCurrent && (
                    <Link
                      to={`${ClientRoutes.ActivitiesPublished}/${activity.versionId}`}
                    >
                      {activity.title}
                    </Link>
                  )}
                  {isCurrent && (
                    <>
                      <Text className={styles.currentVersionLabel}>
                        {activity.title}
                      </Text>
                      <Badge appearance="filled" color="informative">
                        {t("active")}
                      </Badge>
                    </>
                  )}
                </div>
              }
            />

            <Divider />

            <div className={styles.metadata}>
              {/* Version number */}
              <div className={styles.metadataRow}>
                <Text className={styles.metadataLabel}>{t("version")}:</Text>
                <Text>{activity.version}</Text>
              </div>

              {activity.comments && activity.comments[0].message && (
                <div className={styles.metadataRow}>
                  <Text className={styles.metadataLabel}>{t("comments")}:</Text>
                  <Text>{activity.comments[0].message}</Text>
                </div>
              )}

              <div className={styles.metadataRow}>
                <Text className={styles.metadataLabel}>
                  {t("publishedDate")}:
                </Text>
                <Text>
                  {activity.publishedTimeInUTC
                    ? new Date(activity.publishedTimeInUTC).toLocaleString()
                    : "-"}
                </Text>
              </div>

              <div className={styles.metadataRow}>
                <Text className={styles.metadataLabel}>
                  {t("publishedBy")}:
                </Text>
                <Text>{activity.publishedBy?.displayName || "-"}</Text>
              </div>
            </div>

            <div>
              <ActivityHistoryDiffCard
                currentActivityVersion={activity}
                previousActivity={previousActivity}
                activityFields={activityFields}
              />
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default ActivityHistoryList;
